import React, { useState, useEffect, useMemo } from 'react';
import './UserDetail.css';
import { useSetPageContext } from '../contexts/ChatContext';

// Stripe subscription actions offered in the user page. Each one is confirmed
// inline before the request is sent; the main app's webhook applies the result.
const SUB_ACTIONS = {
  period_end: { label: 'Cancel at period end', confirm: 'The subscription stays active until the end of the current period, then ends and the user drops to Free.', done: 'Cancellation scheduled for the end of the period.' },
  now:        { label: 'Cancel immediately',   confirm: 'The subscription ends right now; the user drops to Free as soon as the webhook runs. Unused time is not refunded automatically.', done: 'Subscription cancelled.' },
  keep:       { label: 'Keep subscription',     confirm: 'Removes the scheduled cancellation; the subscription renews normally.', done: 'Scheduled cancellation removed.' },
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');
const money = (cents, currency) => (cents == null ? '' : `${(cents / 100).toFixed(2)} ${(currency || 'usd').toUpperCase()}`);

const UserDetail = ({ userId, onBack }) => {
  const [user, setUser] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Invoice creation form state
  const [invPlan, setInvPlan] = useState('solo');
  const [invInterval, setInvInterval] = useState('month');
  const [invQuantity, setInvQuantity] = useState(2);
  const [invPriceDollars, setInvPriceDollars] = useState('15.00');
  const [invRecurring, setInvRecurring] = useState(false);

  // Stripe subscription panel state
  const [subs, setSubs] = useState(null);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState(null);
  const [subPending, setSubPending] = useState(null); // { id, mode } awaiting confirmation
  const [subBusy, setSubBusy] = useState(false);
  const [subMessage, setSubMessage] = useState(null);
  const [invBusy, setInvBusy] = useState(false);
  const [invResult, setInvResult] = useState(null);
  const [invError, setInvError] = useState(null);

  // Inbox connectivity test state
  const [inboxBusy, setInboxBusy] = useState(false);
  const [inboxResult, setInboxResult] = useState(null);
  const [inboxError, setInboxError] = useState(null);

  // Delete-user state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  // Publish this user to the chatbot so the admin can ask "what's their plan?"
  // without repeating the ID. Auto-clears when UserDetail unmounts.
  const chatCtx = useMemo(() => {
    if (!userId) return null;
    return {
      page: 'user_detail',
      user_id: userId,
      user_email: user?.email || null,
      user_name: user?.name || null,
      daily_plan: user?.daily_plan ?? null,
      status: user?.status ?? null,
    };
  }, [userId, user?.email, user?.name, user?.daily_plan, user?.status]);
  useSetPageContext(chatCtx);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch user details
      const userResponse = await fetch(`/api/users/${userId}`);
      if (!userResponse.ok) {
        throw new Error(`Failed to fetch user: ${userResponse.status}`);
      }
      const userData = await userResponse.json();
      setUser(userData);
      fetchSubscriptions(); // independent of the rest; keeps its own error state

      // Fetch user contacts
      console.log('Fetching contacts for userId:', userId);
      const contactsResponse = await fetch(`/api/users/${userId}/contacts`);
      if (!contactsResponse.ok) {
        throw new Error(`Failed to fetch contacts: ${contactsResponse.status}`);
      }
      const contactsData = await contactsResponse.json();
      console.log('Received contacts data:', contactsData);
      console.log('🔍 CHECKING FIRST CONTACT created_at:', contactsData.contacts?.[0]?.created_at);
      console.log('🔍 FULL FIRST CONTACT:', JSON.stringify(contactsData.contacts?.[0], null, 2));
      // Use the contacts array from the API response
      setContacts(contactsData.contacts || contactsData);

    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load user information');
    } finally {
      setLoading(false);
    }
  };

  const getPlanName = (dailyPlan) => {
    switch (dailyPlan) {
      case 0: return 'Free';
      case 1: return 'Solo ($15/month)';
      case 2: return 'Pro ($20/month)'; 
      case 3: return 'Team ($25/month)';
      default: return 'No Plan';
    }
  };

  // Re-read only the user record, without the full-page spinner. Used after a
  // billing change, once the main app's webhook has had a moment to apply it.
  const refreshUser = async () => {
    try {
      const r = await fetch(`/api/users/${userId}`);
      if (r.ok) setUser(await r.json());
    } catch (e) { /* keep what we have */ }
  };

  const fetchSubscriptions = async () => {
    setSubsLoading(true);
    setSubsError(null);
    try {
      const res = await fetch(`/api/users/${userId}/subscriptions`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setSubs(data);
    } catch (err) {
      setSubsError(err.message || 'Failed to load Stripe subscriptions');
    } finally {
      setSubsLoading(false);
    }
  };

  const runSubAction = async () => {
    if (!subPending) return;
    setSubBusy(true);
    setSubMessage(null);
    try {
      const res = await fetch(`/api/users/${userId}/subscriptions/${subPending.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: subPending.mode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setSubMessage({ ok: true, text: SUB_ACTIONS[subPending.mode].done });
      setSubPending(null);
      await fetchSubscriptions();
      setTimeout(refreshUser, 4000);
    } catch (err) {
      setSubMessage({ ok: false, text: `Action failed: ${err.message}` });
    } finally {
      setSubBusy(false);
    }
  };

  const createInvoice = async () => {
    setInvBusy(true);
    setInvError(null);
    setInvResult(null);

    try {
      const dollars = parseFloat(invPriceDollars);
      if (isNaN(dollars) || dollars < 0) throw new Error('Price must be 0 or greater');
      const unitAmountCents = Math.round(dollars * 100);

      const qty = invPlan === 'team' ? parseInt(invQuantity, 10) : 1;
      if (invPlan === 'team' && (!qty || qty < 2)) {
        throw new Error('Team plan requires at least 2 seats');
      }

      const endpoint = invRecurring ? 'create-subscription' : 'create-invoice';
      const res = await fetch(`/api/users/${userId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: invPlan,
          interval: invInterval,
          quantity: qty,
          unit_amount_cents: unitAmountCents,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setInvResult(data);
      fetchSubscriptions();
      setTimeout(refreshUser, 4000);
    } catch (err) {
      setInvError(err.message || (invRecurring ? 'Failed to create subscription' : 'Failed to create invoice'));
    } finally {
      setInvBusy(false);
    }
  };

  const testInbox = async () => {
    setInboxBusy(true);
    setInboxError(null);
    setInboxResult(null);
    try {
      const res = await fetch(`/api/users/${userId}/test-inbox`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setInboxResult(data);
    } catch (err) {
      setInboxError(err.message || 'Failed to test inbox');
    } finally {
      setInboxBusy(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteConfirmEmail('');
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteBusy) return;
    setDeleteOpen(false);
    setDeleteError(null);
  };

  const confirmDeleteUser = async () => {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_email: deleteConfirmEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const m = data.mongo || {};
      const auth = data.supabase_auth || {};
      const authMsg = auth.attempted
        ? (auth.deleted ? 'Supabase auth user deleted.'
          : auth.error ? `Supabase auth deletion failed: ${auth.error}`
          : auth.note || 'No matching Supabase auth user.')
        : `Supabase auth deletion skipped (${auth.skipped_reason || 'no service key'}).`;
      alert(
        `User deleted.\n\n` +
        `MongoDB:\n` +
        `  • users: ${m.users_deleted}\n` +
        `  • user_contacts: ${m.user_contacts_deleted}\n` +
        `  • user_connectors: ${m.user_connectors_deleted}\n` +
        `  • cis_connectors: ${m.cis_connectors_deleted}\n` +
        `  • contact_rescue_progress: ${m.contact_rescue_progress_deleted}\n\n` +
        authMsg
      );
      setDeleteOpen(false);
      onBack && onBack();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete user');
    } finally {
      setDeleteBusy(false);
    }
  };

  const renderProviderStatus = (label, p) => {
    if (!p) return null;
    let state = 'unknown';
    let text = 'Unknown';
    if (p.can_access_inbox === true) { state = 'ok'; text = 'Connected'; }
    else if (p.has_refresh_token === false) { state = 'warn'; text = 'Not connected'; }
    else if (p.error) { state = 'error'; text = 'Error'; }

    const hint = p.error ? (
      p.error === 'no_refresh_token_stored' ? 'Have the user log in through the OAuth URL.' :
      p.error === 'refresh_failed:invalid_grant' ? 'User must re-consent (token revoked / password changed / 6-month idle).' :
      p.error === 'refresh_failed:invalid_scope' ? 'App permissions changed — user must re-consent.' :
      p.error === 'gmail_api_http_403' ? 'Missing gmail.readonly scope — scope mismatch at consent time.' :
      p.error === 'graph_api_http_401' ? 'Wrong audience/scope — likely v1.0 token against v2.0 Graph.' :
      p.error.startsWith('refresh_failed:http_400') ? 'Transient or config issue — check the provider dashboard.' :
      null
    ) : null;

    return (
      <div className={`inbox-provider inbox-${state}`}>
        <div className="inbox-provider-head">
          <span className="inbox-provider-label">{label}</span>
          <span className={`inbox-badge inbox-badge-${state}`}>{text}</span>
        </div>
        <div className="inbox-provider-body">
          <div>refresh token: <strong>{String(p.has_refresh_token)}</strong></div>
          <div>token valid: <strong>{String(p.token_valid)}</strong></div>
          <div>can access inbox: <strong>{String(p.can_access_inbox)}</strong></div>
          {p.email_address && <div>mailbox: <strong>{p.email_address}</strong></div>}
          {typeof p.messages_total === 'number' && <div>messages: <strong>{p.messages_total.toLocaleString()}</strong></div>}
          {p.error && <div className="inbox-error-code">error: <code>{p.error}</code></div>}
          {hint && <div className="inbox-hint">{hint}</div>}
        </div>
      </div>
    );
  };

  const exportToCSV = () => {
    if (contacts.length === 0) {
      alert('No contacts to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Name',
      'Email',
      'Phone',
      'Additional Phones',
      'Organization',
      'Job Title',
      'Job ID',
      'Source',
      'Date Added',
      'Contact Status',
      'Tags',
      'Company',
      'Address',
      'Given Name',
      'Family Name',
      'Message ID',
      'Origin Source'
    ];

    // Convert contacts to CSV rows
    const csvRows = contacts.map(contact => {
      // USE PROCESSED DATA FROM BACKEND API (not raw contact_data)
      const name = contact.name || 'Unknown Name';
      const email = contact.email || '';
      const phoneNumbers = contact.phoneNumbers || [];
      const primaryPhone = phoneNumbers[0]?.value || '';
      const additionalPhones = phoneNumbers.slice(1).map(p => `${p.type}: ${p.value}`).join('; ');
      const organization = contact.organization || ''; // ✅ From backend processing
      const jobTitle = contact.jobTitle || ''; // ✅ From backend processing
      const jobId = contact.job_id || '';
      const source = contact.source || '';
      
      // Use ONLY created_at (Unix timestamp in seconds)
      let dateAdded = '';
      if (contact.created_at) {
        // created_at is Unix timestamp in seconds, convert to milliseconds
        const date = new Date(contact.created_at * 1000);
        dateAdded = date.toLocaleDateString();
      }
      
      // Extract additional data from raw contact_data for supplementary fields
      const contactData = contact.contact_data || {};
      const contactStatus = contactData.contact_status || '';
      const tags = contactData.contextual_tags || contactData.tags || '';
      const company = organization || contactData.companyName || contactData.company || ''; // Prefer processed
      
      // Extract address info - handle both Office365 and Google formats
      let addressStr = '';
      if (contactData.addresses && Array.isArray(contactData.addresses) && contactData.addresses.length > 0) {
        // Office365 format
        addressStr = contactData.addresses.map(addr => 
          `${addr.street || ''} ${addr.city || ''} ${addr.state || ''} ${addr.postal_code || addr.postalCode || ''}`.trim()
        ).join('; ');
      } else if (contactData.businessAddress) {
        // Single business address
        const addr = contactData.businessAddress;
        addressStr = `${addr.street || ''} ${addr.city || ''} ${addr.state || ''} ${addr.postal_code || addr.postalCode || ''}`.trim();
      } else if (contactData.homeAddress) {
        // Single home address  
        const addr = contactData.homeAddress;
        addressStr = `${addr.street || ''} ${addr.city || ''} ${addr.state || ''} ${addr.postal_code || addr.postalCode || ''}`.trim();
      }
      
      // Extract names from contact_data for CSV completeness
      const names = contactData.names || [];
      const givenName = names[0]?.given_name || contactData.givenName || '';
      const familyName = names[0]?.family_name || contactData.surname || '';
      
      const messageId = contactData.message_id || '';
      const originSource = contactData.origin_source || contact.source || '';

      return [
        name,
        email,
        primaryPhone,
        additionalPhones,
        organization,
        jobTitle,
        jobId,
        source,
        dateAdded,
        contactStatus,
        tags,
        company,
        addressStr,
        givenName,
        familyName,
        messageId,
        originSource
      ].map(field => `"${String(field).replace(/"/g, '""')}"`) // Escape quotes for CSV
    });

    // Combine headers and rows
    const csvContent = [
      headers.map(header => `"${header}"`).join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `contacts_${user?.email || userId}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="user-detail-container">
        <div className="user-detail-header">
          <button onClick={onBack} className="back-button">
            ← Back to Users
          </button>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-detail-container">
        <div className="user-detail-header">
          <button onClick={onBack} className="back-button">
            ← Back to Users
          </button>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchUserData} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-detail-container">
        <div className="user-detail-header">
          <button onClick={onBack} className="back-button">
            ← Back to Users
          </button>
        </div>
        <div className="error-container">
          <p className="error-message">User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-detail-container">
      <div className="user-detail-header">
        <button onClick={onBack} className="back-button">
          ← Back to Users
        </button>
        <h2>User Details</h2>
      </div>

      <div className="user-info-card">
        <div className="user-avatar-large">
          <span>{user.email ? user.email.substring(0, 2).toUpperCase() : 'U'}</span>
        </div>
        <div className="user-details">
          <h3 className="user-email-large">{user.email}</h3>
          {user.name && <p className="user-name-large">{user.name}</p>}
          <p className="user-id-large">ID: {user._id}</p>
          {user.createdAt && (
            <p className="user-date-large">
              Joined: {new Date(user.createdAt).toLocaleDateString()}
            </p>
          )}
          {user.lastActive && (
            <p className="user-date-large">
              Last Active: {new Date(user.lastActive).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="user-status-large">
          <span className={`status-badge ${user.status || 'active'}`}>
            {user.status || 'active'}
          </span>
        </div>
      </div>

      <div className="inbox-section">
        <div className="inbox-header">
          <h3>Inbox Connectivity</h3>
          <button
            onClick={testInbox}
            disabled={inboxBusy}
            className="test-inbox-btn"
          >
            {inboxBusy ? 'Testing…' : 'Test Inbox'}
          </button>
        </div>
        <p className="inbox-help">
          Checks whether Evercontact can currently read this user's Gmail and Office365 mailboxes using their stored OAuth tokens.
        </p>

        {inboxError && <div className="inbox-error-box">{inboxError}</div>}

        {inboxResult && (
          <div className="inbox-result">
            {!inboxResult.user_found && (
              <div className="inbox-error-box">User not found in the main app database.</div>
            )}
            <div className="inbox-providers">
              {renderProviderStatus('Gmail', inboxResult.google)}
              {renderProviderStatus('Office365', inboxResult.office365)}
            </div>
          </div>
        )}
      </div>

      <div className="plan-section">
        <h3>Subscription Plan</h3>
        <div className="plan-info">
          <div className="current-plan">
            <span className="plan-label">Current Plan:</span>
            <span className={`plan-badge plan-${user.daily_plan || 0}`}>
              {getPlanName(user.daily_plan)}
            </span>
          </div>
          <p className="invoice-help">
            Plans are granted through billing only. To give this user access, use the form
            below: a one-off invoice covers a single month or year, a recurring subscription
            renews until it is cancelled in Stripe. Both accept $0 for a free licence. Stripe
            is the source of truth: a plan set by hand would be reverted by the main app's
            daily reconciliation.
          </p>
        </div>
      </div>

      <div className="invoice-section stripe-section">
        <div className="stripe-header">
          <h3>Stripe Subscriptions</h3>
          <div className="stripe-header-actions">
            {subs && subs.customer_url && (
              <a className="stripe-link" href={subs.customer_url} target="_blank" rel="noopener noreferrer">
                Open customer in Stripe →
              </a>
            )}
            <button className="copy-btn" onClick={fetchSubscriptions} disabled={subsLoading}>
              {subsLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {subsError && <div className="invoice-error">{subsError}</div>}
        {subs && !subs.customer_id && (
          <p className="invoice-help">No Stripe customer yet. Granting a license below creates one.</p>
        )}
        {subs && subs.app_state && subs.app_state.admin_invoice_grant && (
          <p className="invoice-note">
            One-off invoice grant on file ({subs.app_state.admin_invoice_grant.plan || 'plan'})
            {subs.app_state.period_end ? `, access until ${fmtDate(subs.app_state.period_end)}` : ''}.{' '}
            <a href={subs.app_state.admin_invoice_grant.url} target="_blank" rel="noopener noreferrer">Open invoice →</a>
          </p>
        )}
        {subs && subs.customer_id && subs.subscriptions.length === 0 && (
          <p className="invoice-help">This customer has no subscriptions in Stripe.</p>
        )}

        {subs && subs.subscriptions.map((s) => (
          <div key={s.id} className={`sub-card ${s.live ? 'sub-card--live' : 'sub-card--ended'}`}>
            <div className="sub-main">
              <span className={`sub-status sub-status--${s.status}`}>{s.status.replace('_', ' ')}</span>
              <strong>{s.plan || 'Unknown plan'}</strong>
              <span className="sub-price">
                {money(s.unit_amount, s.currency)}
                {s.quantity > 1 ? ` × ${s.quantity}` : ''}
                {s.interval ? ` / ${s.interval}` : ''}
              </span>
              <span className="sub-source">{s.source}</span>
            </div>
            <div className="sub-meta">
              {s.created && <span>Started {fmtDate(s.created)}</span>}
              {s.live && !s.cancel_at_period_end && s.current_period_end && <span>Renews {fmtDate(s.current_period_end)}</span>}
              {s.live && s.cancel_at_period_end && <span className="sub-warn">Cancels {fmtDate(s.cancel_at || s.current_period_end)}</span>}
              {!s.live && (s.ended_at || s.canceled_at) && <span>Ended {fmtDate(s.ended_at || s.canceled_at)}</span>}
              {s.collection_method === 'send_invoice' && <span>Billed by invoice</span>}
              <a href={s.url} target="_blank" rel="noopener noreferrer">Open in Stripe →</a>
            </div>
            {s.live && (
              <div className="sub-actions">
                {subPending && subPending.id === s.id ? (
                  <div className="sub-confirm">
                    <span>{SUB_ACTIONS[subPending.mode].confirm}</span>
                    <button className="delete-user-btn" onClick={runSubAction} disabled={subBusy}>
                      {subBusy ? 'Working…' : `Confirm: ${SUB_ACTIONS[subPending.mode].label}`}
                    </button>
                    <button className="copy-btn" onClick={() => setSubPending(null)} disabled={subBusy}>Back</button>
                  </div>
                ) : (
                  <select
                    className="plan-select"
                    value=""
                    onChange={(e) => { if (e.target.value) setSubPending({ id: s.id, mode: e.target.value }); }}
                    disabled={subBusy}
                  >
                    <option value="">Actions…</option>
                    {!s.cancel_at_period_end && <option value="period_end">{SUB_ACTIONS.period_end.label}</option>}
                    <option value="now">{SUB_ACTIONS.now.label}</option>
                    {s.cancel_at_period_end && <option value="keep">{SUB_ACTIONS.keep.label} (undo cancellation)</option>}
                  </select>
                )}
              </div>
            )}
          </div>
        ))}
        {subMessage && (
          <div className={subMessage.ok ? 'invoice-success' : 'invoice-error'}>{subMessage.text}</div>
        )}
      </div>

      <div className="invoice-section">
        <h3>Grant a License</h3>
        <p className="invoice-help">
          Bill the user for a license at any price (including $0). A one-off invoice
          activates the license for one interval when paid. With Recurring on, a Stripe
          subscription renews every interval at this price until it is cancelled in Stripe.
          Team licenses include seat count.
        </p>

        <div className="invoice-form">
          <div className="invoice-field">
            <label htmlFor="inv-plan">License</label>
            <select
              id="inv-plan"
              value={invPlan}
              onChange={(e) => {
                setInvPlan(e.target.value);
                if (e.target.value === 'team' && invQuantity < 2) setInvQuantity(2);
              }}
              disabled={invBusy}
            >
              <option value="solo">Solo</option>
              <option value="pro">Pro</option>
              <option value="team">Team</option>
            </select>
          </div>

          <div className="invoice-field">
            <label htmlFor="inv-interval">Interval</label>
            <select
              id="inv-interval"
              value={invInterval}
              onChange={(e) => setInvInterval(e.target.value)}
              disabled={invBusy}
            >
              <option value="month">Monthly</option>
              <option value="year">Annual</option>
            </select>
          </div>

          {invPlan === 'team' && (
            <div className="invoice-field">
              <label htmlFor="inv-quantity">Seats (min 2)</label>
              <input
                id="inv-quantity"
                type="number"
                min="2"
                step="1"
                value={invQuantity}
                onChange={(e) => setInvQuantity(e.target.value)}
                disabled={invBusy}
              />
            </div>
          )}

          <div className="invoice-field">
            <label htmlFor="inv-price">Unit price (USD, can be 0)</label>
            <input
              id="inv-price"
              type="number"
              min="0"
              step="0.01"
              value={invPriceDollars}
              onChange={(e) => setInvPriceDollars(e.target.value)}
              disabled={invBusy}
            />
          </div>

          <div className="invoice-field">
            <label htmlFor="inv-recurring">
              <input
                id="inv-recurring"
                type="checkbox"
                checked={invRecurring}
                onChange={(e) => setInvRecurring(e.target.checked)}
                disabled={invBusy}
              />
              {' '}Recurring
            </label>
            <small>
              {invRecurring
                ? `Creates a subscription that renews every ${invInterval === 'year' ? 'year' : 'month'} at this price until cancelled in Stripe.`
                : 'One-off invoice: the license lasts one interval, then expires.'}
            </small>
          </div>

          <button
            onClick={createInvoice}
            disabled={invBusy}
            className="create-invoice-btn"
          >
            {invBusy ? 'Creating…' : (invRecurring ? 'Create Subscription' : 'Create Invoice')}
          </button>
        </div>

        {invError && <div className="invoice-error">{invError}</div>}
        {invResult && invResult.subscription_id && (
          <div className="invoice-success">
            <p>
              <strong>Subscription created</strong> — status: {invResult.status},
              {' '}${(invResult.unit_amount / 100).toFixed(2)} × {invResult.quantity} per {invResult.interval}
              {invResult.current_period_end && (
                <>, renews {new Date(invResult.current_period_end).toLocaleDateString()}</>
              )}
            </p>
            {invResult.hosted_invoice_url && (
              <p>
                <a href={invResult.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                  Open first invoice →
                </a>
              </p>
            )}
            <p className="invoice-note">
              {invResult.amount_due === 0
                ? 'Zero-dollar subscription — each renewal is paid automatically. The license activates as soon as the webhook sees the new subscription.'
                : 'The user receives an invoice by email each period (30 days to pay). The license activates now and Stripe marks it past due if an invoice goes unpaid.'}
            </p>
          </div>
        )}
        {invResult && !invResult.subscription_id && (
          <div className="invoice-success">
            <p>
              <strong>Invoice created</strong> — status: {invResult.status},
              total: ${(invResult.total / 100).toFixed(2)}
            </p>
            {invResult.hosted_invoice_url && (
              <p>
                <a href={invResult.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                  Open hosted invoice →
                </a>
                {' '}
                <button
                  className="copy-btn"
                  onClick={() => {
                    navigator.clipboard?.writeText(invResult.hosted_invoice_url);
                    alert('Invoice link copied');
                  }}
                >
                  Copy link
                </button>
              </p>
            )}
            {invResult.amount_due === 0 && (
              <p className="invoice-note">
                Zero-dollar invoice — marked paid automatically. License will activate shortly.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="danger-zone-section">
        <h3>Danger Zone</h3>
        <div className="danger-zone-card">
          <div className="danger-zone-info">
            <strong>Delete this user</strong>
            <p>
              Permanently removes the user document, their stored contacts, OAuth
              connectors, and rescue progress from MongoDB, plus their Supabase
              auth account. Other users are not affected. This cannot be undone.
            </p>
          </div>
          <button onClick={openDeleteModal} className="delete-user-btn">
            Delete User
          </button>
        </div>
      </div>

      {deleteOpen && (
        <div className="delete-modal-backdrop" onClick={closeDeleteModal}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete user permanently?</h3>
            <p className="delete-modal-warn">
              This will remove <strong>{user.email}</strong> from MongoDB
              (user, contacts, connectors, rescue progress) and Supabase auth.
              This cannot be undone.
            </p>
            <label htmlFor="delete-confirm-email" className="delete-modal-label">
              Type the user's email to confirm:
            </label>
            <input
              id="delete-confirm-email"
              type="email"
              value={deleteConfirmEmail}
              onChange={(e) => setDeleteConfirmEmail(e.target.value)}
              placeholder={user.email}
              disabled={deleteBusy}
              className="delete-modal-input"
              autoFocus
            />
            {deleteError && <div className="delete-modal-error">{deleteError}</div>}
            <div className="delete-modal-actions">
              <button
                onClick={closeDeleteModal}
                disabled={deleteBusy}
                className="delete-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteUser}
                disabled={
                  deleteBusy ||
                  deleteConfirmEmail.trim().toLowerCase() !==
                    (user.email || '').trim().toLowerCase()
                }
                className="delete-modal-confirm"
              >
                {deleteBusy ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="contacts-section">
        <div className="contacts-header">
          <h3>User Contacts</h3>
          <div className="contacts-header-actions">
            <span className="contacts-count">{contacts.length} contacts</span>
            {contacts.length > 0 && (
              <button onClick={exportToCSV} className="export-csv-btn">
                📁 Export to CSV
              </button>
            )}
          </div>
        </div>
        
        {contacts.length === 0 ? (
          <div className="no-contacts">
            <p>No contacts found for this user</p>
          </div>
        ) : (
          <div className="contacts-list">
            {contacts.map((contact, index) => {
              // Use the cleaned data from API response
              const displayName = contact.name || 'Unknown Name';
              const emailValue = contact.email || 'No email';
              const phoneNumbers = contact.phoneNumbers || [];
              const primaryPhone = phoneNumbers[0] ? phoneNumbers[0].value : null;
              const photoUrl = contact.photo;
              
              return (
                <div key={contact._id || index} className="contact-item">
                  <div className="contact-avatar">
                    {photoUrl ? (
                      <img src={photoUrl} alt={displayName} className="contact-photo" />
                    ) : (
                      <div className="contact-initials">
                        {displayName.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="contact-info">
                    <h4 className="contact-name">{displayName}</h4>
                    {emailValue && (
                      <p className="contact-email">
                        <span className="contact-label">Email:</span> {emailValue}
                      </p>
                    )}
                    {primaryPhone && (
                      <p className="contact-phone">
                        <span className="contact-label">Phone:</span> {primaryPhone}
                      </p>
                    )}
                    {contact.organization && (
                      <p className="contact-organization">
                        <span className="contact-label">Organization:</span> {contact.organization}
                      </p>
                    )}
                    {contact.jobTitle && (
                      <p className="contact-job-title">
                        <span className="contact-label">Job Title:</span> {contact.jobTitle}
                      </p>
                    )}
                    {contact.job_id && (
                      <p className="contact-job">
                        <span className="contact-label">Job ID:</span> {contact.job_id}
                      </p>
                    )}
                    {phoneNumbers.length > 1 && (
                      <div className="additional-phones">
                        {phoneNumbers.slice(1).map((phone, phoneIndex) => (
                          <p key={phoneIndex} className="contact-phone-alt">
                            <span className="contact-label">{phone.type}:</span> {phone.value}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="contact-meta">
                    {contact.created_at && (
                      <p className="contact-date">
                        Added: {new Date(contact.created_at * 1000).toLocaleDateString()}
                      </p>
                    )}
                    <p className="contact-source">Source: {contact.source || 'Unknown'}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetail;